/**
 * Accueil parent : répond d'abord à « est-ce que mon enfant travaille ? ».
 *
 * 1. Une carte par enfant lié — la semaine (mêmes chiffres que le bilan du
 *    dimanche, migration 0348), les enfants à relancer en premier, et les
 *    seules actions utiles pour CET enfant (son compte, l'abonner, SKILLCARDS).
 * 2. Les invitations en attente.
 * 3. Les 3 dernières nouvelles du Feed.
 * 4. Le bilan de niveau de l'adulte (secondaire, en bas).
 */

import { ChallengeChildButton } from '@/features/private-live/child-challenge';
import { HelpTip } from '@/components/help-tip';
import { AccountSwitcher } from '@/components/admin/account-switcher';
import { DashboardTrousse } from '@/features/trousse/dashboard-trousse';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeftRight, ArrowRight, Baby, Clock, CreditCard, Link2, Sparkles } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { levelLabel } from '@jeraisonne/db/curriculum';
import { useAuth } from '@/lib/auth';
import { isNativeApp } from '@/lib/platform';
import { AvatarCircle } from '@/components/avatar-circle';
import { NewsFeed } from '@/components/news/news-feed';
import { SelfAssessmentCard } from '@/features/skill-diagnostic/self-assessment-card';
import { SwitchToChildDialog } from '@/components/parent/switch-account-dialogs';
import { useRoleNewsFeed, useFeedUnreadCount } from '@/lib/news/use-role-news-feed';
import {
  useChildrenAccess,
  useMyParentSubscriptions,
  type ChildAccess,
} from '@/lib/parent/subscriptions';
import { subWithFreeSeat } from '@/lib/parent/plan-seats';
import { useManagedChildIds } from '@/lib/parent/managed-children';
import { useParentChildLinks } from '@/lib/parent/child-links';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';
import {
  lastActivityLabel,
  sortForAttention,
  useParentChildrenOverview,
  weekHeadline,
  type ChildWeekOverview,
} from '@/lib/parent/overview';

const stripMath = (s: string) => s.replace(/\$([^$]*)\$/g, '$1');

export function ParentDashboardPage() {
  const { profile, user } = useAuth();
  const parentId = user?.id ?? '';
  const overview = useParentChildrenOverview(parentId);
  const links = useParentChildLinks(parentId);
  const access = useChildrenAccess(parentId);
  const managed = useManagedChildIds(parentId);
  const subs = useMyParentSubscriptions(parentId);
  const hasFreeSeat = !!subWithFreeSeat(subs.data ?? []);
  const [switchTo, setSwitchTo] = useState<{ id: string; name: string } | null>(null);

  const accessById = new Map((access.data ?? []).map((a) => [a.child_id, a]));
  const kids = sortForAttention(overview.data ?? []);
  const pending = (links.data ?? []).filter((l) => l.status === 'pending');
  const loading = overview.isLoading || links.isLoading;
  const firstName = (profile?.display_name ?? '').trim().split(' ')[0];

  return (
    <div className="pb-safe mx-auto max-w-2xl space-y-8 px-4 py-6 sm:py-8">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-ink-900 text-2xl font-extrabold">
            Bonjour {firstName} 👋
          </h1>
          <p className="text-ink-500 mt-1 font-sans text-sm">
            {kids.length > 1
              ? 'Voici la semaine de tes enfants.'
              : kids.length === 1
                ? `Voici la semaine de ${kids[0]!.first_name}.`
                : 'Suis le travail de tes enfants, semaine après semaine.'}
          </p>
        </div>
        <div className="flex shrink-0 items-start gap-2 self-start">
          <AccountSwitcher />
          <DashboardTrousse />
        </div>
      </header>
      <SkillcardPromoBanner to="/parent/skillcards" ctaLabel="SKILLCARDS pour mes enfants" />

      {loading ? (
        <div className="bg-ink-100 h-48 animate-pulse rounded-2xl" aria-busy />
      ) : kids.length === 0 && pending.length === 0 ? (
        <NoChildYet />
      ) : (
        <section aria-label="Mes enfants" className="space-y-3">
          {kids.map((c) => (
            <ChildWeekCard
              key={c.student_id}
              child={c}
              access={accessById.get(c.student_id)}
              isManaged={!!managed.data?.has(c.student_id)}
              hasFreeSeat={hasFreeSeat}
              onSwitch={() => setSwitchTo({ id: c.student_id, name: c.first_name })}
            />
          ))}
          {pending.map((p) => (
            <Link
              key={p.link_id}
              to="/parent/children"
              className="flex min-h-[2.75rem] items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 active:scale-[0.98]"
            >
              <Clock className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
              <span className="text-ink-800 min-w-0 flex-1 text-sm">
                <span className="font-semibold">{p.student?.display_name ?? 'Un enfant'}</span> n’a
                pas encore accepté ton invitation.
              </span>
              <ArrowRight className="text-ink-400 h-4 w-4 shrink-0" aria-hidden />
            </Link>
          ))}
        </section>
      )}

      {kids.length > 0 && <LatestNews />}

      {profile?.id && (
        <section aria-label="Pour toi" className="space-y-2">
          <h2 className="font-display text-ink-500 text-xs font-bold uppercase tracking-wider">
            Pour toi
          </h2>
          <SelfAssessmentCard userId={profile.id} role="parent" />
        </section>
      )}

      {switchTo && (
        <SwitchToChildDialog
          childId={switchTo.id}
          childName={switchTo.name}
          onClose={() => setSwitchTo(null)}
        />
      )}
    </div>
  );
}

// ─── Carte « la semaine d'un enfant » ───────────────────────────────────────

const TONE_DOT = {
  good: 'bg-green-500',
  neutral: 'bg-ink-300',
  alert: 'bg-amber-500',
} as const;

export function ChildWeekCard({
  child: c,
  access,
  isManaged,
  hasFreeSeat = false,
  onSwitch,
  now,
}: {
  child: ChildWeekOverview;
  access: ChildAccess | undefined;
  isManaged: boolean;
  /** Un abonnement possédé a une place libre : on l'y ajoute au lieu de racheter. */
  hasFreeSeat?: boolean;
  onSwitch: () => void;
  now?: Date;
}) {
  const head = weekHeadline(c, now);
  const hasAccess = !!access?.has_access;
  const native = isNativeApp();
  const top = c.top_cards[0];
  // Semaine vide : la phrase-bilan suffit, pas de rangée de zéros.
  const weekActive = c.exercises_completed > 0 || c.hora > 0;

  return (
    <article
      aria-label={`La semaine de ${c.first_name}`}
      className="border-ink-100 ocean:bg-white/5 space-y-4 rounded-2xl border bg-white p-4 sm:p-5"
    >
      <div className="flex items-center gap-3">
        <AvatarCircle
          name={access?.display_name ?? c.first_name}
          src={access?.avatar_url}
          size="md"
        />
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 truncate text-base font-extrabold">
            {c.first_name}
          </p>
          <p className="text-ink-500 truncate font-sans text-xs">
            {`${c.level ? `${levelLabel(c.level)} · ` : ''}actif ${lastActivityLabel(c.last_activity_at, now)}`}
          </p>
        </div>
        {access &&
          (hasAccess ? (
            <span className="ocean:bg-green-400/15 ocean:text-green-300 shrink-0 rounded-full bg-green-100 px-2.5 py-0.5 font-sans text-xs font-semibold text-green-700">
              {access.source === 'subscription' ? 'Abonné' : 'Pass'}
            </span>
          ) : (
            <span className="bg-ink-100 text-ink-500 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
              Gratuit
            </span>
          ))}
      </div>

      <p className="text-ink-800 flex items-start gap-2 font-sans text-sm">
        <span
          className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', TONE_DOT[head.tone])}
          aria-hidden
        />
        {head.text}
      </p>

      {weekActive && (
        <dl className="grid grid-cols-4 gap-2 text-center">
          <Stat value={String(c.exercises_completed)} label="exercices" help="parent-exercices" />
          <Stat
            value={c.avg_score == null ? '—' : `${Math.round(c.avg_score)} %`}
            label="réussite"
            help="parent-reussite"
          />
          <Stat value={String(c.hora)} label="HORA" help="parent-hora" />
          <Stat
            value={c.streak_days > 0 ? `${c.streak_days} j` : '—'}
            label="série"
            help="parent-serie"
          />
        </dl>
      )}

      {(top || c.cards_to_work > 0) && (
        <ul className="text-ink-600 space-y-1 font-sans text-xs">
          {top && <li>{`Le plus travaillé : « ${stripMath(top.title)} » (+${top.hora} HORA)`}</li>}
          {c.cards_to_work > 0 && (
            <li>
              {c.cards_to_work > 1
                ? `${c.cards_to_work} SKILLCARDS envoyées par son prof à travailler`
                : '1 SKILLCARD envoyée par son prof à travailler'}
            </li>
          )}
        </ul>
      )}

      <ChallengeChildButton child={{ id: c.student_id, name: c.first_name }} />

      {isManaged && (
        <button
          type="button"
          onClick={onSwitch}
          className="bg-primary-500 hover:bg-primary-600 flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-xl px-3 font-sans text-sm font-semibold text-white active:scale-[0.98]"
        >
          <ArrowLeftRight className="h-4 w-4" aria-hidden />
          Passer sur son compte
        </button>
      )}

      {/* Sans accès : l'offre reste visible mais discrète, sous le suivi. */}
      {!hasAccess && (
        <div className={cn('grid gap-2', native || hasFreeSeat ? 'grid-cols-1' : 'grid-cols-2')}>
          <Link
            to="/parent/subscriptions"
            className="border-ink-200 text-ink-700 hover:border-primary-400 flex min-h-[2.75rem] items-center justify-center gap-1.5 rounded-xl border px-2 font-sans text-sm font-semibold active:scale-[0.98]"
          >
            <CreditCard className="h-4 w-4 shrink-0" aria-hidden />
            {hasFreeSeat ? 'L’abonner' : 'Tout débloquer'}
          </Link>
          {!native && !hasFreeSeat && (
            <Link
              to="/parent/skillcards"
              search={{ child: c.student_id }}
              className="border-ink-200 text-ink-700 hover:border-primary-400 flex min-h-[2.75rem] items-center justify-center gap-1.5 rounded-xl border px-2 font-sans text-sm font-semibold active:scale-[0.98]"
            >
              <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
              SKILLCARDS
            </Link>
          )}
        </div>
      )}
    </article>
  );
}

/** Chiffre de la semaine ; `help` = pastille « ? » (FAQ contextuelle, migration 0362). */
function Stat({ value, label, help }: { value: string; label: string; help?: string }) {
  return (
    <div className="bg-ink-50 ocean:bg-white/5 relative rounded-xl px-1 py-2">
      <dd className="font-display text-ink-900 text-lg font-extrabold leading-tight">{value}</dd>
      <dt className="text-ink-500 font-sans text-[12px] leading-tight">{label}</dt>
      {help && <HelpTip topic={help} label={label} className="absolute bottom-0 right-0" />}
    </div>
  );
}

// ─── Aucun enfant ───────────────────────────────────────────────────────────

function NoChildYet() {
  return (
    <section className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 space-y-4 rounded-2xl border p-5">
      <div>
        <h2 className="font-display text-ink-900 text-lg font-extrabold">Ajoute ton enfant</h2>
        <p className="text-ink-600 mt-1 font-sans text-sm">
          Tu verras ici chaque semaine ce qu’il a travaillé, ses résultats et ce qui reste à faire.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Link
          to="/parent/children"
          search={{ add: 'create' }}
          className="bg-primary-500 hover:bg-primary-600 flex min-h-[2.75rem] flex-1 items-center justify-center gap-1.5 rounded-xl px-3 font-sans text-sm font-semibold text-white active:scale-[0.98]"
        >
          <Baby className="h-4 w-4" aria-hidden />
          Créer son compte
        </Link>
        <Link
          to="/parent/children"
          search={{ add: 'link' }}
          className="border-ink-200 text-ink-800 flex min-h-[2.75rem] flex-1 items-center justify-center gap-1.5 rounded-xl border bg-white px-3 font-sans text-sm font-semibold active:scale-[0.98]"
        >
          <Link2 className="h-4 w-4" aria-hidden />
          Il a déjà un compte
        </Link>
      </div>
    </section>
  );
}

// ─── 3 dernières nouvelles ──────────────────────────────────────────────────

function LatestNews() {
  const { user } = useAuth();
  const { items, authorsById, doneAssignmentKeys, isLoading } = useRoleNewsFeed('parent');
  const unread = useFeedUnreadCount('parent');
  if (!isLoading && items.length === 0) return null;
  return (
    <section aria-label="Dernières nouvelles" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-ink-500 flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
          Dernières nouvelles
          {unread > 0 && (
            <span
              className="bg-primary-500 rounded-full px-2 py-0.5 text-[12px] normal-case tracking-normal text-white"
              aria-label={`${unread} nouvelle${unread > 1 ? 's' : ''}`}
            >
              {unread}
            </span>
          )}
        </h2>
        <Link
          to="/parent/news"
          className="text-primary-600 inline-flex min-h-[2.75rem] shrink-0 items-center gap-1 whitespace-nowrap font-sans text-sm font-semibold"
        >
          Tout voir <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
      <NewsFeed
        items={items.slice(0, 3)}
        isLoading={isLoading}
        viewerRole="parent"
        viewerId={user?.id}
        authorsById={authorsById}
        doneAssignmentKeys={doneAssignmentKeys}
      />
    </section>
  );
}
